import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faQrcode,
  faPrint,
  faRotate,
  faSpinner,
  faGears,
} from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { equipmentService, type EquipmentStatus } from '@/services/equipment.service';
import { useHasPermission } from '@/hooks/use-permissions';
import { SkeletonPage } from '@/components/skeletons';
import { Button } from '@/components/ui/button';
import { toast } from '@/lib/swal';
import { ROUTES } from '@/routes/routes';
import { EquipmentTimeline } from './components/EquipmentTimeline';
import { CustomerInfoCard } from './components/CustomerInfoCard';

const STATUS_BADGE: Record<EquipmentStatus, string> = {
  ACTIVE: 'bg-primary/10 text-primary',
  INACTIVE: 'bg-muted text-muted-foreground',
  RETIRED: 'bg-destructive/10 text-destructive',
};

const STATUS_LABELS: Record<EquipmentStatus, string> = {
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
  RETIRED: 'Retired',
};

function Field({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-sm text-foreground">{value || '—'}</div>
    </div>
  );
}

export function EquipmentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const can = useHasPermission();
  const [printing, setPrinting] = useState(false);

  const { data: equipment, isLoading, isError } = useQuery({
    queryKey: ['equipment', id],
    queryFn: () => equipmentService.get(id!),
    enabled: !!id,
  });

  const regenerateMutation = useMutation({
    mutationFn: () => equipmentService.regenerateQr(id!),
    onSuccess: () => {
      toast.success('QR image regenerated');
      void qc.invalidateQueries({ queryKey: ['equipment', id] });
    },
    onError: () => toast.error('Failed to regenerate QR image'),
  });

  const handlePrint = async () => {
    if (!equipment) return;
    setPrinting(true);
    try {
      await equipmentService.downloadLabel(id!, equipment.assetNumber);
      toast.success('Label PDF downloaded');
      void qc.invalidateQueries({ queryKey: ['equipment', id] });
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 403) {
        toast.error('You do not have permission to print labels.');
      } else {
        toast.error('Failed to generate label PDF');
      }
    } finally {
      setPrinting(false);
    }
  };

  if (isLoading) return <SkeletonPage />;

  if (isError || !equipment) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <FontAwesomeIcon icon={faGears} className="mb-4 h-12 w-12 text-muted-foreground/40" />
        <p className="text-sm text-muted-foreground">Equipment not found.</p>
        <Button variant="ghost" className="mt-4" onClick={() => navigate(ROUTES.EQUIPMENT)}>
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2 h-4 w-4" />
          Back to Equipment
        </Button>
      </div>
    );
  }

  const labelDate = equipment.lastLabelPrintedAt
    ? new Date(equipment.lastLabelPrintedAt).toLocaleString()
    : null;
  const qrDate = equipment.qrCodeGeneratedAt
    ? new Date(equipment.qrCodeGeneratedAt).toLocaleString()
    : null;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Link
          to={ROUTES.EQUIPMENT}
          className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
          aria-label="Back to Equipment"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="h-4 w-4" />
        </Link>
        <div>
          <h1 className="text-lg font-semibold text-foreground">{equipment.equipmentName}</h1>
          <p className="text-xs text-muted-foreground">
            {equipment.assetNumber} · {equipment.serialNumber}
          </p>
        </div>
        <span
          className={`ml-auto inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_BADGE[equipment.status]}`}
        >
          {STATUS_LABELS[equipment.status]}
        </span>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Equipment details */}
        <div className="lg:col-span-2 space-y-4 rounded-lg border border-border bg-card p-6">
          <h2 className="text-sm font-semibold text-foreground">Equipment Details</h2>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <Field label="Asset Number" value={equipment.assetNumber} />
            <Field label="Serial Number" value={equipment.serialNumber} />
            <Field label="Model Number" value={equipment.modelNumber} />
            <Field
              label="Ownership"
              value={equipment.ownershipType === 'COMPANY' ? 'Company Inventory' : 'Customer-Owned'}
            />
            {/*
              D-101 — this page showed ownership but never said WHICH customer.
              One authoritative field, using the same `organizationName ?? name`
              rule as the list, so the two screens cannot disagree. The full
              customer information card (contact, address, service location) is
              Stage D and deliberately not built here.
            */}
            <Field
              label="Customer Organisation"
              value={
                equipment.ownershipType === 'COMPANY'
                  ? 'Company Inventory'
                  : (equipment.customerOrganizationName ?? equipment.customerName)
              }
            />
            {/*
              D-102 (Stage B) - the SERVICE journey, derived server-side from
              existing inspection and inward records. Sits beside, and never
              replaces, the asset-lifecycle Status above: one says where the job
              is, the other says whether the asset is still on the books.
            */}
            <Field label="Workflow Stage" value={equipment.workflowStage ?? 'Not yet in service'} />
            {/*
              D-104 (Stage D) — inward identity and the route into the record the
              workflow stage describes.

              The inward is display-only: this application has an inward LIST
              page but no inward DETAIL route, so there is nothing safe to link
              to. The inspection has a real route (`/inspections/:id`) and is
              therefore a real link.
            */}
            <Field label="Workflow Group" value={equipment.workflowGroup} />
            <Field label="Inward Number" value={equipment.inwardNumber ?? 'No inward recorded'} />
            <Field
              label="Inward Date"
              value={
                equipment.inwardDate
                  ? new Date(equipment.inwardDate).toLocaleDateString()
                  : 'No inward recorded'
              }
            />
            {equipment.workflowInspectionId ? (
              <div className="min-w-0">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Current Record
                </p>
                <Link
                  to={ROUTES.INSPECTION_DETAIL.replace(':id', equipment.workflowInspectionId)}
                  className="mt-0.5 block truncate text-sm text-primary underline-offset-4 hover:underline"
                >
                  Open inspection
                </Link>
              </div>
            ) : null}
            <Field
              label="Purchase Date"
              value={equipment.purchaseDate ? new Date(equipment.purchaseDate).toLocaleDateString() : null}
            />
            <Field
              label="Installation Date"
              value={equipment.installationDate ? new Date(equipment.installationDate).toLocaleDateString() : null}
            />
          </div>
          {(equipment.description || equipment.notes) && (
            <div className="mt-2 grid grid-cols-1 gap-4 sm:grid-cols-2 border-t border-border pt-4">
              {equipment.description && <Field label="Description" value={equipment.description} />}
              {equipment.notes && <Field label="Notes" value={equipment.notes} />}
            </div>
          )}
        </div>

        {/* QR Identity card */}
        <div className="rounded-lg border border-border bg-card p-6 space-y-4">
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faQrcode} className="h-4 w-4 text-primary" />
            <h2 className="text-sm font-semibold text-foreground">Equipment Identity</h2>
          </div>

          <div className="flex justify-center">
            {equipment.qrCodeImageUrl ? (
              <img
                src={equipment.qrCodeImageUrl}
                alt={`QR code for ${equipment.assetNumber}`}
                className="h-40 w-40 rounded-lg border border-border object-contain"
              />
            ) : (
              <div className="flex h-40 w-40 flex-col items-center justify-center rounded-lg border border-dashed border-border bg-muted/30 text-muted-foreground">
                <FontAwesomeIcon icon={faQrcode} className="h-10 w-10 opacity-30" />
                <span className="mt-2 text-xs">Not generated</span>
              </div>
            )}
          </div>

          {qrDate && (
            <p className="text-center text-xs text-muted-foreground">Generated {qrDate}</p>
          )}

          <div className="rounded-lg bg-muted/30 p-3 space-y-1">
            <div className="flex justify-between text-xs">
              <span className="text-muted-foreground">Label Prints</span>
              <span className="font-medium text-foreground">{equipment.labelPrintedCount}</span>
            </div>
            {labelDate && (
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Last Printed</span>
                <span className="font-medium text-foreground">{labelDate}</span>
              </div>
            )}
          </div>

          <div className="space-y-2">
            {can('equipment:print-label') && (
              <Button
                className="w-full"
                onClick={handlePrint}
                disabled={printing}
              >
                {printing ? (
                  <FontAwesomeIcon icon={faSpinner} className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <FontAwesomeIcon icon={faPrint} className="mr-2 h-4 w-4" />
                )}
                Print Label (A4)
              </Button>
            )}
            {can('equipment:regenerate-qr') && (
              <Button
                variant="secondary"
                className="w-full"
                onClick={() => regenerateMutation.mutate()}
                disabled={regenerateMutation.isPending}
              >
                {regenerateMutation.isPending ? (
                  <FontAwesomeIcon icon={faSpinner} className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <FontAwesomeIcon icon={faRotate} className="mr-2 h-4 w-4" />
                )}
                Regenerate QR Image
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* D-104 — the same card the drill-down uses; one implementation. */}
      {equipment.customerId ? <CustomerInfoCard customerId={equipment.customerId} /> : null}

      <EquipmentTimeline equipmentId={id!} />
    </div>
  );
}
